import styled from 'styled-components';

import { Text } from '../../../../components';
import { ANIM, COLORS, SPACE } from '@/nova/theme/tokens';
import { hoverWash } from '@/nova/theme/mixins';

/* The sub-tab's content, on the same rhythm as the carousel tabs: room below the tabs, then the intro and blocks. */
export const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[28]};
  margin-top: ${SPACE[16]};
`;

export const Lead = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  max-width: 72ch;
  font-size: 14px;
  line-height: 1.55;
  color: ${COLORS.textMuted};
`;

/* The sub-tab glyph: sized like the shared Icon it sits beside in the tab strip. */
export const GlyphSvg = styled.svg<{ $size: number }>`
  flex: 0 0 auto;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

export const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

export const BlockHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/* Pushes a block's action (Regenerate) to the end of its head row. */
export const HeadAction = styled.div`
  margin-left: auto;
`;

export const RunLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  max-width: 420px;
`;

export const RunNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.text};
`;

export const BlockName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 650;
  color: ${COLORS.heading};
`;

export const Chip = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  border: 1px solid ${COLORS.hairline};
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
`;

export const Note = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/* Dimmed while a regeneration redraws the cards: what shows is about to be replaced. Never fewer than two columns
   (a column is at most half the row, less half the gap), so a tile spanning two always fits. */
export const TileGrid = styled.div<{ $busy?: boolean }>`
  opacity: ${({ $busy }) => ($busy ? 0.5 : 1)};
  transition: opacity ${ANIM.INTERACT_MS}ms ease;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(150px, calc(50% - ${SPACE[12]} / 2)), 1fr));
  gap: ${SPACE[12]};
`;

/* A tile with a new image drawn for it spans two columns: the current image and the new one side by side. */
export const Tile = styled.figure<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  margin: 0;
  min-width: 0;
  grid-column: ${({ $wide }) => ($wide ? 'span 2' : 'auto')};
`;

export const ComparePair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACE[8]};
`;

export const CompareSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
`;

export const SideLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

/* A tile's own note: why it can't be redrawn, that a new image is being drawn, how long one waits. */
export const TileNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

export const TileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

/* Redraw one image: the outline Lumio action at tile size, its cost (the Lumio glyph and the credits) after the label.
   The cost wraps under the label on the narrowest tiles rather than overflowing them. */
export const RedrawButton = styled.button`
  ${hoverWash}
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[2]} ${SPACE[8]};
  width: 100%;
  box-sizing: border-box;
  min-height: 34px;
  padding: ${SPACE[4]} ${SPACE[12]};
  border-radius: 99px;
  border: 1px solid ${COLORS.ctaBorder};
  background: ${COLORS.ctaOutlineBg};
  color: ${COLORS.ctaText};
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;

export const RedrawLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

export const RedrawCost = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/* A card image sits whole on a muted plate: the cut-outs are transparent, and the squares are shown uncropped. */
export const TileArt = styled.div<{ $aspect: string }>`
  aspect-ratio: ${({ $aspect }) => $aspect};
  border-radius: 12px;
  overflow: hidden;
  background: ${COLORS.surfaceMuted};
`;

export const TileImg = styled.img<{ $cover?: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: ${({ $cover }) => ($cover ? 'cover' : 'contain')};
`;

export const TileLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SharedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${SPACE[24]};
`;

export const SharedImage = styled(Tile)`
  width: 240px;
  max-width: 100%;
`;

export const NavPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

export const NavIcons = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const NavIcon = styled.li`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${COLORS.surfaceInset};

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

export const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${SPACE[12]};
`;
